import { useState } from 'react';
import { validateForm, validateTeamName } from '../utils/validation';
import { getPreferences, savePreferences, deletePreferences } from '../services/api';
import useCognitoAuth from '../authorization/cognitoAuthorizer';

const EMPTY_FORM = {
  teamNames: [] as string[],
  reminderMethod: 'sms',
  phoneNumber: '',
  telegramChatId: '',
  remindersEnabled: true,
  remindersTime: 3
};

/** Builds form state from API preferences, mapping the legacy single teamName onto teamNames. */
const toFormState = (prefs: any) => {
  const { teamName, teamNames, ...rest } = prefs;
  return {
    ...EMPTY_FORM,
    ...rest,
    teamNames: teamNames && teamNames.length > 0 ? teamNames : teamName ? [teamName] : []
  };
};

export const useFormState = (initialPreferences?: any, onNotification?: (type: string, message: string, header?: string) => void) => {
  const { authToken } = useCognitoAuth();
  const [formState, setFormState] = useState(initialPreferences ? toFormState(initialPreferences) : EMPTY_FORM);
  const [teamInput, setTeamInput] = useState('');

  const [errors, setErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [hasPreferences, setHasPreferences] = useState(!!initialPreferences);
  const [showTelegramBanner, setShowTelegramBanner] = useState(false);

  const clearError = (field: string) => {
    setErrors(prev => {
      if (!prev[field]) {
        return prev;
      }
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  const handleInputChange = (field: string, value: any) => {
    setFormState(prev => ({ ...prev, [field]: value }));
    clearError(field);
  };

  const handleTeamInputChange = (value: string) => {
    setTeamInput(value);
    clearError('teamNames');
  };

  const addTeam = (name: string, teams: string[]): string[] | null => {
    const error = validateTeamName(name, teams);
    if (error) {
      setErrors(prev => ({ ...prev, teamNames: error }));
      return null;
    }
    return [...teams, name.trim()];
  };

  const handleAddTeam = () => {
    const teams = addTeam(teamInput, formState.teamNames);
    if (teams) {
      setFormState(prev => ({ ...prev, teamNames: teams }));
      setTeamInput('');
      clearError('teamNames');
    }
  };

  const handleRemoveTeam = (index: number) => {
    setFormState(prev => ({ ...prev, teamNames: prev.teamNames.filter((_: string, i: number) => i !== index) }));
    clearError('teamNames');
  };

  const handleSubmit = async () => {
    let stateToSave = formState;
    if (teamInput.trim()) {
      const teams = addTeam(teamInput, formState.teamNames);
      if (!teams) {
        return;
      }
      stateToSave = { ...formState, teamNames: teams };
      setFormState(stateToSave);
      setTeamInput('');
    }

    const newErrors = validateForm(stateToSave);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    const success = await savePreferences(stateToSave, authToken);
    if (success) {
      setHasPreferences(true);
      setIsEditing(false);
      setShowTelegramBanner(stateToSave.reminderMethod === 'telegram');
      onNotification?.('success', 'Your reminder preferences have been saved successfully.');
    } else {
      onNotification?.('error', 'Unable to save your preferences. Please try again.', 'Save failed');
    }
  };

  const handleCancel = async () => {
    if (hasPreferences) {
      const prefs = await getPreferences(authToken);
      if (prefs) {
        setFormState(toFormState(prefs));
      }
    }
    setTeamInput('');
    setErrors({});
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleOptIn = () => {
    setIsEditing(true);
  };

  const handleOptOut = async () => {
    const success = await deletePreferences(authToken);
    if (success) {
      setFormState(EMPTY_FORM);
      setTeamInput('');
      setHasPreferences(false);
      setIsEditing(false);
      onNotification?.('success', 'Your data has been removed successfully.');
    } else {
      onNotification?.('error', 'Unable to opt out. Please try again.', 'Opt out failed');
    }
  };

  return {
    formState,
    teamInput,
    errors,
    isEditing,
    hasPreferences,
    showTelegramBanner,
    handleInputChange,
    handleTeamInputChange,
    handleAddTeam,
    handleRemoveTeam,
    handleSubmit,
    handleCancel,
    handleEdit,
    handleOptIn,
    handleOptOut,
    dismissTelegramBanner: () => setShowTelegramBanner(false)
  };
};
